import React, { useRef, useState, useEffect } from 'react';
import { useAuthenticatedFetch } from '../../utils/api';

/**
 * Reusable Optimization Type Filter Component
 * Displays a menu with optimization type selection options (fetched from API)
 * 
 * @param {string} optimizationTypeFilter - Current selected filter
 * @param {Function} onChange - Handler when filter changes
 * @param {boolean} disabled - Whether the menu button is disabled
 */
function OptimizationTypeFilter({ optimizationTypeFilter, onChange, disabled = false }) {
  const menuRef = useRef(null);
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  useEffect(() => {
    // Only fetch once on mount
    if (fetchedRef.current) return;
    
    async function fetchTypes() {
      fetchedRef.current = true;
      try {
        const response = await authenticatedFetch('/api/optimization-types');
        const data = await response.json();
        if (data.success && data.types) {
          // Map API types to filter format
          const typeFilters = [
            { value: 'all', label: 'All' },
            ...data.types.map(t => ({ value: t.key, label: t.title }))
          ];
          setTypes(typeFilters);
        } else {
          // Fallback to hardcoded types
          setTypes([
            { value: 'all', label: 'All' },
            { value: 'regular', label: 'Regular' },
            { value: 'moderate', label: 'Moderate' },
            { value: 'extreme', label: 'Extreme' }
          ]);
        }
      } catch (error) {
        console.error('Error fetching optimization types:', error);
        // Fallback to hardcoded types on error
        setTypes([
          { value: 'all', label: 'All' },
          { value: 'regular', label: 'Regular' },
          { value: 'moderate', label: 'Moderate' },
          { value: 'extreme', label: 'Extreme' }
        ]);
      } finally {
        setLoading(false);
      }
    }
    fetchTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only fetch once on mount

  // Find the current selected filter label
  const selectedFilter = types.find(f => f.value === optimizationTypeFilter) || types[0] || { value: 'all', label: 'All' };

  const handleFilterChange = (value) => {
    onChange(value);
    // Close the menu after selection by clicking outside or using available methods
    requestAnimationFrame(() => {
      const menu = menuRef.current;
      if (menu) {
        // Try to close the menu using available methods
        if (typeof menu.hide === 'function') {
          menu.hide();
        } else if (typeof menu.hideOverlay === 'function') {
          menu.hideOverlay();
        } else {
          // Fallback: simulate a click outside the menu to close it
          const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: globalThis
          });
          document.body.dispatchEvent(clickEvent);
        }
      }
    });
  };

  return (
    <>
      <s-button
        commandFor="optimization-type-filter-menu"
        variant="secondary"
        disabled={disabled}
      >
        {selectedFilter.label}
      </s-button>
      
      <s-menu 
        ref={menuRef}
        id="optimization-type-filter-menu" 
        accessibilityLabel="Optimization type filter"
      >
        {types.map((filter) => (
          <s-button
            key={filter.value}
            onClick={() => handleFilterChange(filter.value)}
            disabled={disabled || loading}
          >
            {filter.label}
          </s-button>
        ))}
      </s-menu>
    </>
  );
}

export default OptimizationTypeFilter;

